import { API_URL } from "./apiConfig";

const TOKEN_KEY = "token";
const ROLE_KEY = "role";
const REMEMBER_KEY = "auth_remember";

let refreshPromise = null;
let fetchInstalled = false;

function readStorage(key) {
  return sessionStorage.getItem(key) || localStorage.getItem(key) || "";
}

function writeStorage(key, value, remember) {
  const target = remember ? localStorage : sessionStorage;
  const other = remember ? sessionStorage : localStorage;
  target.setItem(key, value);
  other.removeItem(key);
}

function isApiRequest(url) {
  return typeof url === "string" && (
    url.startsWith(`${API_URL}/`) ||
    url.startsWith("/api/") ||
    url === API_URL
  );
}

function isAuthEndpoint(url) {
  return typeof url === "string" && /\/auth\/(login|refresh|logout)$/.test(url);
}

function decodeJwtExp(token) {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const json = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    return typeof json.exp === "number" ? json.exp : null;
  } catch {
    return null;
  }
}

// Considera o token expirado com uma folga para evitar corrida com o servidor.
function isAccessTokenExpired(token, skewSeconds = 10) {
  const exp = decodeJwtExp(token);
  if (!exp) return false;
  return Date.now() >= (exp - skewSeconds) * 1000;
}

function mergeHeaders(initHeaders) {
  const headers = new Headers(initHeaders || {});
  return headers;
}

function notifyAuthChanged(kind = "changed") {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("auth:changed", { detail: { kind } }));
  }
}

export function getAccessToken() {
  return readStorage(TOKEN_KEY);
}

export function getRole() {
  return readStorage(ROLE_KEY);
}

export function hasSession() {
  return !!getAccessToken();
}

export function getRememberPreference() {
  return readStorage(REMEMBER_KEY) !== "false";
}

export function setSession(token, role, remember = true) {
  if (!token) return;
  writeStorage(TOKEN_KEY, token, remember);
  if (role) writeStorage(ROLE_KEY, role, remember);
  writeStorage(REMEMBER_KEY, String(remember), remember);
  notifyAuthChanged("session");
}

export function clearSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(ROLE_KEY);
  localStorage.removeItem(REMEMBER_KEY);
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(ROLE_KEY);
  sessionStorage.removeItem(REMEMBER_KEY);
  notifyAuthChanged("logout");
}

export async function logout({ remote = true } = {}) {
  if (remote) {
    try {
      await fetch(`${API_URL}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch {
    }
  }
  clearSession();
}

export async function refreshAccessToken() {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const response = await fetch(`${API_URL}/auth/refresh`, {
        method: "POST",
        credentials: "include",
      });

      if (!response.ok) {
        clearSession();
        return null;
      }

      const data = await response.json();
      if (!data?.token) {
        clearSession();
        return null;
      }

      setSession(data.token, data.role, getRememberPreference());
      return data.token;
    } catch {
      clearSession();
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

// Token válido por pelo menos `minValiditySeconds`, renovando antes se preciso. Para requisições
// que não passam pelo fetch interceptado (ex.: upload via XMLHttpRequest com barra de progresso).
export async function getFreshAccessToken(minValiditySeconds = 60) {
  const token = getAccessToken();
  if (token && !isAccessTokenExpired(token, minValiditySeconds)) return token;
  return refreshAccessToken();
}

export async function bootstrapSession() {
  if (getAccessToken()) return true;
  const token = await refreshAccessToken();
  return !!token;
}

export function installAuthFetch(onUnauthorized) {
  if (fetchInstalled || typeof window === "undefined") return;
  fetchInstalled = true;

  const nativeFetch = window.fetch.bind(window);

  window.fetch = async (input, init = {}) => {
    const url = typeof input === "string" ? input : input?.url;
    if (!isApiRequest(url)) {
      return nativeFetch(input, init);
    }

    const originalInit = { ...init };
    const headers = mergeHeaders(originalInit.headers);
    let token = getAccessToken();

    // Renova proativamente se o access token já estiver expirado,
    // evitando disparar uma requisição fadada ao 401.
    if (token && !isAuthEndpoint(url) && isAccessTokenExpired(token)) {
      token = (await refreshAccessToken()) || "";
      if (!token && onUnauthorized) onUnauthorized();
    }

    if (token && !isAuthEndpoint(url)) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    const requestInit = {
      ...originalInit,
      headers,
      credentials: "include",
    };

    let response = await nativeFetch(input, requestInit);

    if (
      response.status === 401 &&
      !isAuthEndpoint(url) &&
      !originalInit.__skipAuthRetry
    ) {
      const refreshedToken = await refreshAccessToken();
      if (!refreshedToken) {
        if (onUnauthorized) onUnauthorized();
        return response;
      }

      const retryHeaders = mergeHeaders(originalInit.headers);
      retryHeaders.set("Authorization", `Bearer ${refreshedToken}`);
      response = await nativeFetch(input, {
        ...originalInit,
        headers: retryHeaders,
        credentials: "include",
        __skipAuthRetry: true,
      });
    }

    return response;
  };
}
